/**
 * Phase 21 — customer Recap page.
 *
 * Route: /recap/:recapId
 *
 * Shows the rendered share-card PNG (1080×1920) plus a text rendition
 * of the payload (totals, top merchant, longest streak, milestones,
 * highlight). The "Share" button uses navigator.share with the PNG as
 * a File when supported (D-share); otherwise falls back to a download
 * anchor so the customer can still save the image and post it
 * manually.
 */
import { useEffect, useState } from "react";
import { Link, Redirect, useRoute } from "wouter";

import { useCustomerRecap } from "@orbit/api-client-react";

import { useAuth } from "../hooks/useAuth";
import { Wordmark } from "../components/Brand";
import { BottomNav } from "../components/BottomNav";

function pesos(centavos: number): string {
  if (!Number.isFinite(centavos) || centavos <= 0) return "₱0";
  return `₱${Math.round(centavos / 100).toLocaleString("en-US")}`;
}

function num(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

export function RecapPage() {
  const { isAuthenticated } = useAuth();
  const [, params] = useRoute<{ recapId: string }>("/recap/:recapId");
  const recapId = params?.recapId ?? null;
  const recapQ = useCustomerRecap(recapId);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (recapId) document.title = "Your Orbit Recap";
  }, [recapId]);

  if (!isAuthenticated) return <Redirect to="/login" />;

  if (recapQ.isLoading) {
    return (
      <Shell>
        <p className="text-sm text-ink-dark">Loading your recap…</p>
      </Shell>
    );
  }
  if (recapQ.isError || !recapQ.data) {
    return (
      <Shell>
        <p className="text-sm text-red-600">Couldn't load this recap.</p>
        <Link
          href="/cards"
          className="inline-block mt-3 text-sm text-navy underline"
        >
          Back to cards
        </Link>
      </Shell>
    );
  }

  const recap = recapQ.data;
  const p = recap.payload;
  const imageUrl = recap.imageUrl;

  async function handleShare() {
    if (shareBusy) return;
    setShareBusy(true);
    setShareError(null);
    try {
      const res = await fetch(imageUrl, { credentials: "include" });
      if (!res.ok) throw new Error(`Image fetch failed (${res.status})`);
      const blob = await res.blob();
      const file = new File([blob], `orbit-recap-${recap.id}.png`, {
        type: "image/png",
      });
      const nav = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
        share?: (data: ShareData) => Promise<void>;
      };
      if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({
          title: "My Orbit recap",
          text: `Check out my ${recap.kind === "ANNUAL" ? "year" : "month"} on Orbit (${recap.periodLabel}).`,
          files: [file],
        });
      } else {
        // Fallback — trigger a download.
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `orbit-recap-${recap.id}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      // AbortError = user cancelled — silent.
      if ((err as Error).name !== "AbortError") {
        setShareError((err as Error).message);
      }
    } finally {
      setShareBusy(false);
    }
  }

  return (
    <Shell>
      <header className="mb-4">
        <p className="text-xs uppercase tracking-wide text-amber-dark font-medium">
          {recap.kind === "ANNUAL" ? "Year in Orbit" : "Monthly recap"}
        </p>
        <h1 className="text-2xl font-semibold text-navy">
          {recap.periodLabel}
        </h1>
        <p className="text-sm text-ink-dark mt-1">
          For {recap.customerName}
        </p>
      </header>

      <section className="mb-4 rounded-xl overflow-hidden bg-navy/5">
        {imgError ? (
          <div className="aspect-[9/16] flex items-center justify-center text-ink-dark text-sm p-8 text-center">
            Couldn't load your share-card image.
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={`Orbit ${recap.kind} recap for ${recap.periodLabel}`}
            className="w-full h-auto"
            onError={() => setImgError(true)}
            data-testid="recap-share-image"
          />
        )}
      </section>

      <div className="flex gap-2 mb-6">
        <button
          type="button"
          onClick={() => void handleShare()}
          disabled={shareBusy || imgError}
          className="flex-1 px-4 py-3 rounded-lg bg-navy text-white font-medium disabled:opacity-50"
          data-testid="recap-share-button"
        >
          {shareBusy ? "Preparing…" : "Share your recap"}
        </button>
      </div>
      {shareError && (
        <p className="text-sm text-red-600 mb-4">{shareError}</p>
      )}

      <section className="space-y-4 mb-8">
        <h2 className="text-base font-semibold text-navy">By the numbers</h2>
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Visits" value={num(p.totals.visits)} />
          <Stat
            label="Active merchants"
            value={num(p.totals.activeMerchants)}
          />
          <Stat label="Points earned" value={num(p.totals.pointsEarned)} />
          <Stat label="Stamps earned" value={num(p.totals.stampsEarned)} />
          <Stat
            label="Vouchers redeemed"
            value={num(p.totals.vouchersRedeemed)}
          />
          <Stat
            label="Estimated savings"
            value={pesos(p.totals.estimatedSavingsCentavos)}
          />
        </div>
      </section>

      {(p.topMerchantByVisits || p.topMerchantBySpendCentavos) && (
        <section className="mb-8">
          <h2 className="text-base font-semibold text-navy mb-2">
            Top merchants
          </h2>
          <div className="space-y-2">
            {p.topMerchantByVisits && (
              <div className="flex justify-between items-center bg-white border border-amber-light rounded-lg p-3">
                <div>
                  <div className="text-xs text-ink-dark">Most visits</div>
                  <div className="font-semibold text-navy">
                    {p.topMerchantByVisits.tenantName}
                  </div>
                </div>
                <div className="text-amber-dark font-medium">
                  {num(p.topMerchantByVisits.visits)} visits
                </div>
              </div>
            )}
            {p.topMerchantBySpendCentavos && (
              <div className="flex justify-between items-center bg-white border border-amber-light rounded-lg p-3">
                <div>
                  <div className="text-xs text-ink-dark">Most spend</div>
                  <div className="font-semibold text-navy">
                    {p.topMerchantBySpendCentavos.tenantName}
                  </div>
                </div>
                <div className="text-amber-dark font-medium">
                  {pesos(p.topMerchantBySpendCentavos.spendCentavos)}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {p.longestStreak && (
        <section className="mb-8">
          <h2 className="text-base font-semibold text-navy mb-2">
            Longest active streak
          </h2>
          <div className="bg-white border border-amber-light rounded-lg p-3">
            <div className="font-semibold text-navy">
              {p.longestStreak.tenantName}
            </div>
            <div className="text-sm text-ink-dark">
              {num(p.longestStreak.streakCount)}{" "}
              {p.longestStreak.cadence.toLowerCase()} in a row
              {p.longestStreak.isPersonalRecord && (
                <span className="ml-2 inline-block bg-amber text-navy px-2 py-0.5 rounded-full text-xs font-semibold">
                  Personal best
                </span>
              )}
            </div>
          </div>
        </section>
      )}

      {p.milestonesHit.length > 0 && (
        <section className="mb-8">
          <h2 className="text-base font-semibold text-navy mb-2">
            Milestones unlocked
          </h2>
          <ul className="space-y-1">
            {p.milestonesHit.map((m, i) => (
              <li
                key={`${m.tenantId}-${m.milestoneKind}-${m.threshold}-${i}`}
                className="text-sm text-navy flex justify-between bg-white border border-amber-light rounded p-2"
              >
                <span>
                  {m.milestoneKind === "VISIT_COUNT"
                    ? `${m.threshold} visits`
                    : m.milestoneKind === "TENURE_DAYS"
                      ? `${m.threshold} days as a SUKI`
                      : `${pesos(m.threshold)} spent`}
                </span>
                <span className="text-ink-dark">at {m.tenantName}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link
        href="/cards"
        className="inline-block text-sm text-navy underline"
      >
        Back to cards
      </Link>
    </Shell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-amber-light rounded-lg p-3">
      <div className="text-xs text-ink-dark">{label}</div>
      <div className="text-2xl font-bold text-navy mt-1">{value}</div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <header className="border-b border-amber-light bg-white px-4 py-3 flex items-center justify-between">
        <Link href="/cards">
          <Wordmark />
        </Link>
      </header>
      <main className="flex-1 max-w-md mx-auto w-full px-4 py-6">
        {children}
      </main>
      <BottomNav />
    </div>
  );
}
